import React from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { alpha, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import handleOpenWhatsApp from 'services/whatsappService';
import { hero, WHATSAPP_DEFAULT, WHATSAPP_PHONE } from '../cherrySoftwareContent';
import TnoMediaLabLogo, { MEDIA_LAB_TAGLINES, CherryDevWordmark } from 'components/TnoMediaLabLogo';
import { HeroIllustration } from './cherrySoftwareIllustrations';
import FloatingDecor from './FloatingDecor';

const HERO_OUTER_PADDING_X = { xs: 2.5, sm: 3.5, md: 4.5 };
const HERO_OUTER_PADDING_Y = { xs: 2, sm: 3, md: 4 };
const PANEL_PADDING_X = { xs: 3, sm: 4, md: 5 };
const PANEL_PADDING_Y = { xs: 3, sm: 4, md: 5 };

const openWhatsApp = () => handleOpenWhatsApp(null, WHATSAPP_DEFAULT, WHATSAPP_PHONE);

const Hero = () => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  return (
    <Box
      position="relative"
      overflow="hidden"
      paddingX={HERO_OUTER_PADDING_X}
      paddingY={HERO_OUTER_PADDING_Y}
    >
      <Box
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        sx={{
          background: `
            radial-gradient(circle at 12% 18%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 40%),
            radial-gradient(circle at 88% 8%, ${alpha(theme.palette.secondary.main, 0.16)} 0%, transparent 36%),
            radial-gradient(circle at 70% 85%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 30%),
            linear-gradient(135deg, ${alpha(theme.palette.alternate.main, 0.5)} 0%, transparent 55%)
          `,
          pointerEvents: 'none',
        }}
      />

      <FloatingDecor
        icons={[
          { id: 'code', icon: 'code', top: '12%', right: '8%', size: 48, color: 'primary', animate: true },
          { id: 'api', icon: 'autorenew', bottom: '18%', right: '14%', size: 36, color: 'primary', animate: true, delay: '2s' },
        ]}
      />

      <Grid container spacing={{ xs: 3, md: 5 }} alignItems="stretch" position="relative" zIndex={1}>
        <Grid item xs={12} md={7}>
          <Box
            data-aos={isMd ? 'fade-right' : 'fade-up'}
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            paddingX={PANEL_PADDING_X}
            paddingY={PANEL_PADDING_Y}
          >
            <TnoMediaLabLogo
              variant="cherry"
              tagline={MEDIA_LAB_TAGLINES.software}
              alt={hero.brand}
              height={{ xs: 52, md: 68 }}
              width="auto"
              sx={{ mb: { xs: 3, md: 4 } }}
            />

            <Box display="flex" flexWrap="wrap" marginBottom={3} style={{ gap: 8 }}>
              {hero.highlights.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.common.white, 0.9),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  }}
                />
              ))}
            </Box>

            <Typography
              variant="h1"
              color="textPrimary"
              marginBottom={3}
              sx={{
                fontWeight: 800,
                lineHeight: 1.12,
                fontSize: { xs: '2rem', sm: '2.45rem', md: '2.85rem' },
              }}
            >
              {hero.title}
            </Typography>

            <Typography
              variant="h2"
              component="p"
              color="textSecondary"
              marginBottom={4}
              sx={{
                fontWeight: 400,
                lineHeight: 1.75,
                fontSize: { xs: '1.05rem', md: '1.2rem' },
              }}
            >
              {hero.subtitle}
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<WhatsAppIcon />}
              endIcon={<ArrowForwardIcon />}
              onClick={openWhatsApp}
              sx={{
                px: 4,
                py: 1.6,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '1rem',
                boxShadow: `0 14px 36px ${alpha(theme.palette.secondary.main, 0.38)}`,
              }}
            >
              {hero.cta}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            data-aos={isMd ? 'fade-left' : 'fade-up'}
            height="100%"
            borderRadius={4}
            bgcolor={alpha(theme.palette.common.white, 0.78)}
            border={`1px solid ${alpha(theme.palette.primary.main, 0.1)}`}
            boxShadow={`0 24px 60px ${alpha(theme.palette.common.black, 0.05)}`}
            display="flex"
            flexDirection="column"
            paddingX={PANEL_PADDING_X}
            paddingY={PANEL_PADDING_Y}
            style={{ backdropFilter: 'blur(10px)' }}
          >
            <Box
              display="inline-flex"
              alignSelf="flex-start"
              marginBottom={3}
              paddingX={2}
              paddingY={1}
              borderRadius={999}
              bgcolor={alpha(theme.palette.secondary.main, 0.12)}
              border={`1px solid ${alpha(theme.palette.secondary.main, 0.2)}`}
            >
              <Typography
                variant="caption"
                component="div"
                sx={{
                  fontWeight: 800,
                  color: 'primary.main',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <CherryDevWordmark color="inherit" />
                <Box component="span"> · Full Stack</Box>
              </Typography>
            </Box>
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              paddingX={1}
              paddingY={1}
            >
              <HeroIllustration
                primary={theme.palette.primary.main}
                secondary={theme.palette.secondary.main}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Hero;
